import type { JSX } from 'react';
import { useContext, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { buttonBaseClasses, buttonColorVariants } from '@/constants/buttonStyles';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { useBillingItemRefresh } from '@/hooks/useBillingItemRefresh';
import { useMarkAsBillable } from '@/hooks/useEncounters';
import { Button } from '@/ui/Button';
import Badge from '@/ui/components/Badge';
import LegacyButton from '@/ui/components/Button';
import DualTime from '@/ui/components/DualTime';
import Spinner from '@/ui/components/Spinner';
import formatStaffName from '@/utils/formatStaffName';
import { PEOPLE_MARK_NON_BILLABLE, hasPermission } from '@/utils/userPermission';

import { Props } from './types';

const NonBillableBillingItem = ({
  billingItem,
  compact = false,
  encounterType
}: Props): JSX.Element | null => {
  const { permissions } = useContext(AppContext);
  const { encounterId } = useParams();

  const canUpdateBillableStatus = useMemo((): boolean => {
    const requiredPermission = PEOPLE_MARK_NON_BILLABLE;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const { mutateAsync: markAsBillable, isPending: isMarkingAsBillable } = useMarkAsBillable({
    encounterId: encounterId as string,
    encounterType
  });

  const { mutateAsync: refreshBillingItem, isPending: isPendingRefresh } = useBillingItemRefresh(
    {}
  );

  const isItemBillable = useMemo((): boolean => {
    if (!billingItem) return true;
    return (
      !billingItem.non_billable_at &&
      !billingItem.non_billable_by &&
      !billingItem.non_billable_reason
    );
  }, [billingItem]);

  const handleMarkAsBillable = async () => {
    if (!billingItem || !billingItem.id) return;
    await markAsBillable();
    await refreshBillingItem({ billingItemId: billingItem.id });
  };

  const renderMarkBillableAction = () =>
    isItemBillable ? (
      <Link
        aria-label="Mark Non billable"
        to={`/encounters/${encounterType}/by_id/${encounterId}/mark-non-billable?billing_item_id=${billingItem?.id}`}
        state={{ headerName: 'Mark Non Billable', encounterType }}
        className={`${buttonBaseClasses} ${
          buttonColorVariants[Color.YELLOW]
        } p-2 inline-flex justify-center w-full mt-4 rounded-lg`}>
        Mark As Non Billable
      </Link>
    ) : (
      <LegacyButton
        color={Color.GREEN}
        className="mt-3 w-full rounded-lg"
        loadingWithSpinner={isMarkingAsBillable || isPendingRefresh}
        onClick={handleMarkAsBillable}>
        {isMarkingAsBillable || isPendingRefresh ? <Spinner /> : 'Mark as billable'}
      </LegacyButton>
    );

  // The compact card carries the billable state in the button itself, so the
  // badge and the reason footer stay on the full view.
  if (compact) {
    if (!canUpdateBillableStatus) return null;

    return isItemBillable ? (
      <Link
        aria-label="Mark Non billable"
        to={`/encounters/${encounterType}/by_id/${encounterId}/mark-non-billable?billing_item_id=${billingItem?.id}`}
        state={{ headerName: 'Mark Non Billable', encounterType }}>
        <Button className="whitespace-nowrap" color="warning" size="xs">
          Mark As Non Billable
        </Button>
      </Link>
    ) : (
      <Button
        className="whitespace-nowrap"
        color="primary"
        isLoading={isMarkingAsBillable || isPendingRefresh}
        onClick={handleMarkAsBillable}
        size="xs">
        Mark as billable
      </Button>
    );
  }

  if (!billingItem)
    return <div className="my-2">{canUpdateBillableStatus && renderMarkBillableAction()}</div>;

  const billableBadgeLabel = isItemBillable ? 'Billable' : 'Non billable';
  const billableBadgeColor = isItemBillable ? Color.GREEN : Color.RED;

  const renderNonBillableReasonFooter = () => (
    <p data-testid="non-billable-footer" className="text-xs leading-5 text-gray-500 my-2">
      Marked non billable{' '}
      <span>
        ({billingItem.non_billable_reason && <> {billingItem.non_billable_reason.name} </>})
      </span>{' '}
      on <DualTime dateTime={new Date(billingItem.non_billable_at as string)} />
      <span>
        {billingItem.non_billable_by && (
          <> by {formatStaffName({ staff: billingItem.non_billable_by })}</>
        )}
      </span>
    </p>
  );

  return (
    <div className="my-2">
      <div className="flex justify-between mb-4">
        <Badge color={billableBadgeColor} text={billableBadgeLabel} />
      </div>
      {canUpdateBillableStatus && renderMarkBillableAction()}
      {!isItemBillable && renderNonBillableReasonFooter()}
    </div>
  );
};

export default NonBillableBillingItem;
